// src/App.tsx
import { useEffect, useState, Component, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/useAuthStore';
import { useThemeStore } from './stores/useThemeStore';
import { LoadingPage } from './pages/LoadingPage';
import { ErrorPage } from './pages/ErrorPage';
import { DashboardPage } from './pages/DashboardPage';
import { MenuPage } from './pages/MenuPage';
import { CartPage } from './pages/CartPage';
import { PaymentPage } from './pages/PaymentPage';
import { ConfirmationPage } from './pages/ConfirmationPage';
import { OrderConfirmationPage } from './pages/OrderConfirmationPage';
import { ScheduledOrdersPage } from './pages/ScheduledOrdersPage';
import { IdentificationPage } from './pages/IdentificationPage';
import { PaymentSplitPage } from './pages/PaymentSplitPage';
import { TermsAndConditionsPage } from './pages/TermsAndConditionsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { ProfilePage } from './pages/ProfilePage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorModal } from './components/ErrorModal';
import { CheckoutSuccess } from './pages/checkout/CheckoutSuccess';
import { CheckoutFailed } from './pages/checkout/CheckoutFailed';
import { CheckoutPending } from './pages/checkout/CheckoutPending';
import { SessionClosedPage } from './pages/SessionClosedPage';
import { OrderTypePage } from './pages/OrderTypePage';
import { OrderTimingPage } from './pages/OrderTimingPage';
import { OrderModePage } from './pages/OrderModePage';
import { PublicMenuPage } from './pages/PublicMenuPage';
import { SimpleMenuPage } from './pages/SimpleMenuPage';
import { SimpleDashboardPage } from './pages/SimpleDashboardPage';
import { SimplePaymentPage } from './pages/SimplePaymentPage';
import { apiClient } from './lib/api';


import { GoogleOAuthProvider } from '@react-oauth/google';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "123456789-placeholder.apps.googleusercontent.com";

// Error Boundary para capturar errores de renderizado de librerías externas
class GoogleErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("GoogleOAuthProvider Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.children;
    }
    return this.props.children;
  }
}

// Componente para manejar errores de carga del script de Google (común en modo incógnito/bloqueadores)
function SafeGoogleAuthProvider({ children }: { children: React.ReactNode }) {
  const [hasError, setHasError] = useState(false);
  const isSimpleUX = window.location.pathname.includes('/simple-');

  // Si estamos en Simple UX, no necesitamos Google Auth y evitamos posibles colisiones de DOM
  if (isSimpleUX || !GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID.includes('placeholder') || hasError) {
    return <>{children}</>;
  }

  return (
    <GoogleErrorBoundary>
      <GoogleOAuthProvider
        clientId={GOOGLE_CLIENT_ID}
        onScriptLoadError={() => setHasError(true)}
      >
        {children}
      </GoogleOAuthProvider>
    </GoogleErrorBoundary>
  );
}

function App() {
  const { isDark } = useThemeStore();
  const { sessionToken, isTakeAway, tenant } = useAuthStore();
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [initialRedirect, setInitialRedirect] = useState<string | null>(null);

  // Forzar dark mode desde el inicio
  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.body.classList.add('dark');
  }, []);

  useEffect(() => {
    if (sessionToken) {
      apiClient.setSessionToken(sessionToken);
    }
  }, [sessionToken]);

  useEffect(() => {
    const { clientToken } = useAuthStore.getState();
    if (clientToken) {
      apiClient.setClientToken(clientToken);
    }
  }, []);

  // Also watch for changes if we want reactivity, though usually it changes on login/logout
  const clientToken = useAuthStore(state => state.clientToken);
  useEffect(() => {
    if (clientToken) {
      apiClient.setClientToken(clientToken);
    }
  }, [clientToken]);

  // Register session closed callback
  useEffect(() => {
    apiClient.setOnSessionClosed(() => {
      const state = useAuthStore.getState();
      if (state.isTakeAway) {
        state.setSessionClosed(true);
      } else {
        window.location.href = '/session-closed';
      }
    });
  }, []);

  useEffect(() => {
    // Mantener dark mode siempre activo
    document.documentElement.classList.add('dark');
    document.body.classList.add('dark');
  }, [isDark]);

  // Apply service theme (take-away vs dine-in) globally by toggling a root class
  useEffect(() => {
    const root = document.documentElement;

    let targetClass = isTakeAway ? 'theme-orange' : 'theme-sky';

    // Si el branding es Kiero, ignorar el modo de servicio para el tema principal
    if (tenant?.partner_branding === 'KIERO') {
      targetClass = 'theme-kiero';
    }

    root.classList.remove('theme-orange', 'theme-sky', 'theme-kiero');
    root.classList.add(targetClass);

    // Also apply to body for good measure
    document.body.classList.remove('theme-orange', 'theme-sky', 'theme-kiero');
    document.body.classList.add(targetClass);
  }, [isTakeAway, tenant?.partner_branding]);

  // Inicialización de la app y validación de sesión
  useEffect(() => {
    const initializeApp = async () => {
      const startTime = Date.now();
      const MIN_LOADING_TIME = 1800; // Pedido por el usuario: un poco más de tiempo

      try {
        const { sessionToken, identifier, validateSession } = useAuthStore.getState();

        // 1. Detectar si entramos por un token de QR/Mesa en la URL
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        // El primer segmento suele ser el token si no es una ruta fija (login, error, etc.)
        const knownRoutes = ['login', 'error', 'session-closed', 'public-menu', 'simple-menu', 'simple-dashboard', 'simple-payment', 'checkout', 'profile'];
        const potentialToken = pathParts[0];

        if (potentialToken && !knownRoutes.includes(potentialToken) && potentialToken.length > 10) {
          // Es un posible token de QR. Vamos a pre-validarlo para ganar tiempo y evitar redirecciones extras
          try {
            const response = await apiClient.validateSession(potentialToken);
            if (response && response.tenant_uuid) {
              if (response.session_token) {
                apiClient.setSessionToken(response.session_token);
              }

              // Obtener config para saber el UX mode
              const configResp: any = await apiClient.getConfig();
              if (configResp?.success && configResp?.config) {
                const config = configResp.config;
                const features = config.plan?.features;
                const uxMode = (features?.Ux || (features as any)?.ux || '').toLowerCase();

                if (uxMode === 'simple') {
                  // Redirección directa al menú simple optimizada
                  setInitialRedirect(`/simple-menu/${potentialToken}`);
                }
              }
            }
          } catch (e) {
            console.warn("Pre-fetch tenant config failed, falling back to normal flow", e);
          }
        }

        // 2. Validación de sesión estándar si ya existía
        if (sessionToken && identifier) {
          await validateSession(identifier);
        }
      } catch (error) {
        console.error("Initialization error:", error);
      } finally {
        // Asegurar el tiempo mínimo de carga pedido por el usuario
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

        setTimeout(() => {
          setInitializing(false);
        }, remainingTime);
      }
    };

    initializeApp();
  }, []); // Array vacío para ejecutar solo al montar

  // Manejar errores globales
  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('Global error:', event.error);
      setGlobalError('Ha ocurrido un error inesperado. Por favor contacta a soporte.');
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      setGlobalError('Ha ocurrido un error inesperado. Por favor contacta a soporte.');
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  if (initializing) {
    return <LoadingPage />;
  }

  return (
    <Router>
      <SafeGoogleAuthProvider>
        <div className="min-h-screen flex justify-center" style={{ backgroundColor: 'var(--color-bg-page)' }}>
          {/* Main Application Container - Mobile Width on Desktop */}
          <div className="w-full max-w-md mx-auto shadow-[0_0_50px_rgba(0,0,0,0.3)] dark:shadow-[0_0_50px_rgba(0,0,0,0.5)] relative min-h-screen transition-colors" style={{ backgroundColor: 'var(--color-bg-page)' }}>
            <Routes>
              {/* Login Route */}
              <Route path="/login" element={<IdentificationPage />} />

              {/* Identification Route - for JWT tokens */}
              <Route
                path="/:token"
                element={
                  initialRedirect
                    ? <Navigate to={initialRedirect} replace />
                    : <IdentificationPage />
                }
              />

              {/* Terms and Privacy Routes - with JWT token */}
              <Route path="/:token/tyc" element={<TermsAndConditionsPage />} />
              <Route path="/:token/privacy" element={<PrivacyPolicyPage />} />

              {/* Public Menu Route */}
              <Route path="/public-menu/:tenantUuid" element={<PublicMenuPage />} />

              {/* Simple UX Routes (Foodtruck) - No Auth Required */}
              <Route path="/simple-menu/:token" element={<SimpleMenuPage />} />
              <Route path="/simple-dashboard/:token" element={<SimpleDashboardPage />} />
              <Route path="/simple-payment/:token" element={<SimplePaymentPage />} />

              {/* Error Route (mantener para errores críticos) */}
              <Route path="/error" element={<ErrorPage />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard/:tableId"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/menu/:tableId"
                element={
                  <ProtectedRoute>
                    <MenuPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/cart/:tableId"
                element={
                  <ProtectedRoute>
                    <CartPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-timing/:tableId"
                element={
                  <ProtectedRoute>
                    <OrderTimingPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/order-mode/:tableId"
                element={
                  <ProtectedRoute>
                    <OrderModePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/order-type/:tableId"
                element={
                  <ProtectedRoute>
                    <OrderTypePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/order-confirmation/:tableId"
                element={
                  <ProtectedRoute>
                    <OrderConfirmationPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/scheduled-orders/:tableId"
                element={
                  <ProtectedRoute>
                    <ScheduledOrdersPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/payment-split/:tableId"
                element={
                  <ProtectedRoute>
                    <PaymentSplitPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/payment/:tableId"
                element={
                  <ProtectedRoute>
                    <PaymentPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/confirmation/:tableId"
                element={
                  <ProtectedRoute>
                    <ConfirmationPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />

              {/* Redirect root to loading with a sample table ID */}
              <Route path="/" element={<Navigate to="/error" replace />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/failed" element={<CheckoutFailed />} />
              <Route path="/checkout/pending" element={<CheckoutPending />} />

              <Route path="/session-closed" element={<SessionClosedPage />} />

              <Route path="*" element={<Navigate to="/error" replace />} />
            </Routes>

            {/* Error Modal Global */}
            {globalError && (
              <ErrorModal
                isOpen={!!globalError}
                onClose={() => setGlobalError(null)}
                title="Error del Sistema"
                message={globalError}
              />
            )}
          </div>
        </div>
      </SafeGoogleAuthProvider>
    </Router>
  );
}

export default App;